import { create } from 'zustand';
interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  ranch_id: string;
}
interface AuthState {
  user: AuthUser | null;
  ranch: { id: string; name: string } | null;
  token: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  setAuth: (user: AuthUser, ranch: { id: string; name: string }, token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
}
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  ranch: null,
  token: null,
  isLoading: true,
  isInitialized: false,
  setAuth: (user, ranch, token) => set({ user, ranch, token, isLoading: false }),
  clearAuth: () => {
    localStorage.removeItem('hollerhub_token');
    set({ user: null, ranch: null, token: null, isLoading: false });
  },
  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),
}));
export async function authFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('hollerhub_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(path, { ...init, headers });
  const json = await res.json();
  if (res.status === 401) {
    useAuthStore.getState().clearAuth();
    throw new Error('Unauthorized');
  }
  if (!res.ok || !json.success) {
    throw new Error(json.error || 'Request failed');
  }
  return json.data as T;
}
export async function login(email: string, password: string) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'Login failed');
  const { token, user, ranch } = json.data;
  localStorage.setItem('hollerhub_token', token);
  useAuthStore.getState().setAuth(user, ranch, token);
  return json.data;
}
export async function register(name: string, email: string, password: string, ranch_name: string) {
  const res = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password, ranch_name }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'Registration failed');
  const { token, user, ranch } = json.data;
  localStorage.setItem('hollerhub_token', token);
  useAuthStore.getState().setAuth(user, ranch, token);
  return json.data;
}
export async function logout() {
  try {
    await authFetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // ignore
  }
  useAuthStore.getState().clearAuth();
}
export async function checkAuth() {
  const token = localStorage.getItem('hollerhub_token');
  if (!token) {
    useAuthStore.getState().setLoading(false);
    useAuthStore.getState().setInitialized(true);
    return;
  }
  try {
    const data = await authFetch<{ user: AuthUser; ranch: { id: string; name: string } }>('/api/auth/me');
    useAuthStore.getState().setAuth(data.user, data.ranch, token);
  } catch {
    useAuthStore.getState().clearAuth();
  }
  useAuthStore.getState().setInitialized(true);
}